import { useEffect, useState, useRef, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import Alert from '@mui/material/Alert';
import CropFreeIcon from '@mui/icons-material/CropFreeOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Link from '@mui/material/Link';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import type { Scene as SceneType, CameraMode, SceneActionsData } from '@/types/scene.types';
import { SimulationOverlay } from '@/components/SimulationOverlay';
import { useMujocoSimulation } from '@/hooks/useMujocoSimulation';
import { THOR_COLORS } from '@/theme/thorTheme';
import Header from '@/components/Header';
import { SceneNavigation } from '@/components/SceneNavigation';
import { fetchScene, fetchSceneActions } from '@/api/Scene';
import { preloadTarModel, getMujocoApp } from '@/mujoco/mujocoInitializer';
import Loading from '@/components/Loading';

export const Scene = () => {
  const { slug } = useParams<{ slug: string }>();

  // Local state - no need for global context
  const [scene, setScene] = useState<SceneType | undefined>(undefined);
  // @ts-expect-error
  const [sceneActions, setSceneActions] = useState<SceneActionsData | undefined>(undefined);
  const [cameraMode, setCameraMode] = useState<CameraMode>('top-down');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const { error: simulationError, isLoading: simulationLoading } = useMujocoSimulation(
    'mujoco-sim',
    true, // Always enable simulation
    cameraMode,
    scene
  );

  // Load scene from URL parameter on initial mount or when slug changes
  // Use ref to track the last loaded slug to prevent infinite loops
  const loadedSlugRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Only process if slug changed from what we last loaded
    if (slug === loadedSlugRef.current) {
      return;
    }

    async function loadSceneFromUrl() {
      if (!slug) return;

      // Clear stale scene if it doesn't match the URL
      if (scene && scene.slug !== slug) {
        console.log(`URL changed from scene ${scene.slug} to ${slug}, clearing stale scene`);
        setScene(undefined);
      }

      // Load the scene for this slug
      try {
        const sceneToLoad = await fetchScene(slug);
        setScene(sceneToLoad);
        loadedSlugRef.current = slug;
      } catch (error: unknown) {
        console.error('Failed to load scene from URL:', error);
      }
    }

    loadSceneFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]); // Intentionally excluding scene to prevent infinite loop

  // Preload TAR file in the background when scene is selected AND matches URL
  useEffect(() => {
    // Only preload if the selected scene matches the URL parameter
    if (scene?.tarPath && scene.slug === slug) {
      console.log('⏬ [Scene] Preloading TAR file in background:', scene.tarPath);
      preloadTarModel(scene.tarPath).catch((error: unknown) => {
        console.warn('Background preload failed (will extract on demand):', error);
      });
    }
  }, [scene?.tarPath, scene?.slug, slug]);

  // Load actions JSON when scene is loaded
  useEffect(() => {
    async function loadActions() {
      console.log('📋 [Scene] Load actions effect running', {
        hasScene: !!scene,
        actionsJsonFile: scene?.actionsJsonFile,
      });

      if (!scene?.actionsJsonFile) {
        console.log('📋 [Scene] No actionsJsonFile, clearing actions');
        setSceneActions(undefined);
        return;
      }

      try {
        console.log(`📋 [Scene] Loading actions from ${scene.actionsJsonFile}...`);
        const actionsData = await fetchSceneActions(scene.actionsJsonFile);
        console.log(`✅ [Scene] Loaded actions data:`, actionsData);
        setSceneActions(actionsData);
        console.log(`✅ [Scene] Set ${actionsData.actions.length} actions to state`);
      } catch (error: unknown) {
        console.error('❌ [Scene] Failed to load scene actions:', error);
        setSceneActions(undefined);
      }
    }

    loadActions();
  }, [scene]);

  // Auto-start animation when entering 3rd-person mode
  useEffect(() => {
    if (cameraMode === '3rd-person') {
      const timer = setTimeout(() => {
        const app = getMujocoApp();
        if (app) {
          app.playActions();
          console.log('🎬 Started action playback (camera mode changed to 3rd-person)');
        }
      }, 100);
      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [cameraMode]);

  const cameraModeOptions = [
    { value: 'top-down' as const, label: 'Top-down', icon: CropFreeIcon },
    { value: '1st-person' as const, label: '1st person', icon: PersonIcon },
    { value: '3rd-person' as const, label: '3rd person', icon: RemoveRedEyeIcon },
  ];

  const currentMode =
    cameraModeOptions.find((option) => option.value === cameraMode) ?? cameraModeOptions[0];
  const CurrentIcon = currentMode?.icon ?? CropFreeIcon;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuSelect = (mode: typeof cameraMode) => {
    setCameraMode(mode);
    handleMenuClose();
  };

  const handleViewClick = (index: number) => {
    console.log('View button clicked - switching to 3rd-person mode');
    const app = getMujocoApp();
    console.log(`------------ handleViewClick ${index}`);
    setCameraMode('3rd-person');
    if (app) {
      app.setActiveActionIndex(index);
      app.reset();
      // Reset robot to initial position and start playback
      app.resetPlayback();
    }
  };

  const handleReplayClick = () => {
    console.log('Replay button clicked - resetting scene and restarting action playback');
    const app = getMujocoApp();
    if (app) {
      // Reset the entire scene (closes oven doors, resets object positions, etc.)
      app.reset();
      // Reset robot to initial position and start playback
      app.resetPlayback();
      // Resume playback (workaround for toggle logic in playActions)
      app.playActions();
      console.log('🔄 Scene reset and action playback restarted');
    }
  };

  const objectOverlays = (): ReactNode => {
    const app = getMujocoApp();
    if (app) {
      const overlays = app.getObjectOverlays();

      if (overlays) {
        return overlays.map((x: any, index: number) => {
          return (
            <SimulationOverlay
              onViewClick={() => {
                handleViewClick(index);
              }}
              key={index}
              show={cameraMode === 'top-down'}
              x={x.screenPosition.x * 100}
              y={x.screenPosition.y * 100}
              object_name={x.name}
            />
          );
        });
        // }

        // </Box>
      }
    }
    return null;
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <AppBar
        position="static"
        color="default"
        sx={{
          backgroundColor: 'rgba(10, 50, 53, 0.9)',
          '--Paper-overlay': 'none',
          backgroundImage: 'none',
        }}
      >
        <Toolbar>
          <Link
            href="/environment"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: '1.2rem',
              color: THOR_COLORS.lightGreen,
              '&:hover': {
                color: THOR_COLORS.lightGreen,
                opacity: 0.8,
              },
            }}
          >
            <ArrowBackIcon />
            Back to environments
          </Link>
          <Divider orientation="vertical" sx={{ mx: 2, height: '24px' }} />
          <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
            {scene?.name}
          </Typography>

          {cameraMode !== 'top-down' && (
            <>
              {isSmallScreen ? (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleMenuOpen}
                    endIcon={<ArrowDropDownIcon />}
                    startIcon={<CurrentIcon />}
                    sx={{ ml: 'auto', height: '36px', whiteSpace: 'nowrap' }}
                  >
                    {currentMode?.label ?? 'Top-down'}
                  </Button>
                  <Menu
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    onClose={handleMenuClose}
                  >
                    {cameraModeOptions.map((option) => {
                      const OptionIcon = option.icon;
                      return (
                        <MenuItem
                          key={option.value}
                          selected={option.value === cameraMode}
                          onClick={() => {
                            handleMenuSelect(option.value);
                          }}
                        >
                          <OptionIcon sx={{ mr: 1 }} />
                          {option.label}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </>
              ) : (
                <ToggleButtonGroup
                  value={cameraMode}
                  exclusive
                  onChange={(_, newMode) => {
                    if (newMode !== null) {
                      setCameraMode(newMode);
                    }
                  }}
                  sx={{
                    ml: 'auto',
                    height: '36px',
                    '& .MuiToggleButton-root': {
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <ToggleButton value="top-down" aria-label="top-down">
                    <CropFreeIcon sx={{ mr: 1 }} />
                    Top-down
                  </ToggleButton>
                  {/* <ToggleButton value="1st-person" aria-label="1st-person">
                    <PersonIcon sx={{ mr: 1 }} />
                    1st person
                  </ToggleButton> */}

                  <ToggleButton value="3rd-person" aria-label="3rd-person">
                    <RemoveRedEyeIcon sx={{ mr: 1 }} />
                    3rd person
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Global Loading Overlay - Shows during scene loading regardless of view mode */}
      <Loading isLoading={simulationLoading} />

      {/* Simulation View - Always shows MuJoCo canvas with optional overlay */}
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {/* MuJoCo Simulation Canvas */}
        <Box
          id="mujoco-sim"
          sx={{
            width: '100%',
            height: '100%',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            position: 'relative',
          }}
        />
        {}
        {/* Pink "View" button overlay - only shown in top-down mode */}
        {objectOverlays()}

        {/* Action controls - only shown in 3rd-person mode */}
        {cameraMode === '3rd-person' && (
          <Tooltip title="Replay action" placement="left">
            <IconButton
              onClick={handleReplayClick}
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                backgroundColor: 'rgba(240, 82, 156, 0.9)',
                color: THOR_COLORS.cream,
                '&:hover': {
                  backgroundColor: 'rgba(240, 82, 156, 1)',
                },
                width: 56,
                height: 56,
              }}
            >
              <ReplayIcon sx={{ fontSize: 32 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {simulationError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Simulation Error: {simulationError.message}
        </Alert>
      )}
      {!simulationLoading && cameraMode !== 'top-down' && (
        <SceneNavigation startExpanded={true} cameraMode={cameraMode} />
      )}
    </Box>
  );
};
